/**
 * GID-202: the DOM half of the live activity feed.
 *
 * Split out from taskpane.js because every defect this feature has had lived
 * here, not in the wording: the element was destroyed with its host, it was
 * mounted outside the message list, and it rendered as an empty box when it
 * had nothing to say. taskpane.js cannot be loaded in a test (it reaches for
 * Office, the backend and a dozen services on import), so that logic was
 * effectively untestable until it moved here.
 *
 * The view owns one element for the life of a request and MOVES it between
 * hosts. It never rebuilds it, so nothing already on screen is lost when the
 * first streamed token removes the typing indicator.
 */

import { formatElapsed } from "./activityFeed.js";

const STRIP_ID = "activity-strip";
const TICK_MS = 1000;

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderEntry(entry, isCurrent, elapsedText) {
  const detail = entry.detail
    ? `<div class="activity-detail">${escapeHtml(entry.detail)}</div>`
    : "";
  // Only the line still in progress carries a clock; a finished stage showing
  // a running total would read as though it were still going.
  const elapsed = isCurrent
    ? `<span class="activity-elapsed">${escapeHtml(elapsedText)}</span>`
    : "";
  const note = entry.note
    ? `<div class="activity-note">${escapeHtml(entry.note)}</div>`
    : "";
  // The pane is ~460px wide. Detail and note are their own rows rather than
  // sharing the header row: pushed right with margin-left:auto they collided
  // with a wrapped route label in the real task pane, which a wider browser
  // window never showed.
  return `
    <div class="activity-line${isCurrent ? " is-current" : " is-done"}">
      <div class="activity-head">
        <span class="activity-mark" aria-hidden="true"></span>
        <span class="activity-text">${escapeHtml(entry.text)}</span>
        ${elapsed}
      </div>
      ${detail}
      ${note}
    </div>
  `;
}

/**
 * @param {{entries: () => Array}} log - an activity log from activityFeed.js
 * @param {Document} [doc] - injectable for tests
 * @returns {{mountInto: Function, render: Function, teardown: Function}}
 */
export function createActivityStripView(log, options = {}) {
  const doc = options.doc || (typeof document !== "undefined" ? document : null);
  const now = options.now || (() => Date.now());

  let element = null;
  let startedAt = null;
  let ticker = null;

  function ensureElement() {
    if (element) return element;
    element = doc.createElement("div");
    element.id = STRIP_ID;
    element.className = "activity-strip";
    // The status role sits on the element that persists for the whole request,
    // so a screen reader follows one region rather than several short-lived ones.
    element.setAttribute("role", "status");
    element.setAttribute("aria-live", "polite");
    return element;
  }

  function render() {
    if (!element) return;

    const entries = log.entries();
    if (entries.length === 0) {
      element.innerHTML = "";
      element.hidden = true;
      return;
    }

    const elapsedText = formatElapsed(now() - (startedAt ?? now()));
    element.hidden = false;
    element.innerHTML = entries
      .map((entry, index) => renderEntry(entry, index === entries.length - 1, elapsedText))
      .join("");
  }

  return {
    /**
     * Put the feed at the top of a message's content, above the answer text.
     *
     * Safe to call repeatedly: the same element is re-parented, so a re-mount
     * after the host was removed carries across whatever it was showing.
     */
    mountInto(messageContent) {
      if (!messageContent || !doc) return;
      const strip = ensureElement();
      // The clock belongs to the request, not to any one host: re-parenting on
      // the first streamed token must not restart it.
      if (startedAt === null) startedAt = now();
      if (ticker === null) ticker = setInterval(render, TICK_MS);
      messageContent.insertBefore(strip, messageContent.firstChild);
      render();
    },

    render,

    /** Remove the feed: the work it describes is over. */
    teardown() {
      if (ticker !== null) {
        clearInterval(ticker);
        ticker = null;
      }
      if (element) element.remove();
      element = null;
      startedAt = null;
    },
  };
}
